/* istanbul ignore file */
import { useCallback, useState } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import useEventCallback from '@zenbusiness/application-commons-hooks/useEventCallback';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { RELEASE_FINANCE_HUB } from '#client/constants/experimentsFlags';
import {
  FINANCE_HUB, MONEY_PRO, INVOICES, BANKING, TAX_SERVICES
} from '#client/constants/navItemsIds';

import * as Styled from './BusinessEntityNav.styles';
import { MenuItem } from './components/MenuItem';
import { useBusinessEntityNav } from './hooks/useBusinessEntityNav';
import { useDrawerState } from '../../hooks/useDrawerState';
import { Logo } from '../Logo';

function BusinessEntityNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleDrawer } = useDrawerState();

  const [activeItem, setActiveItem] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const { loading, menu } = useBusinessEntityNav({ isMobile });
  const closeMenu = useEventCallback(() => setExpanded(false));
  const openMenu = useEventCallback(() => setExpanded(true));
  const releaseFinanceHub = useExperimentFlag(RELEASE_FINANCE_HUB, false);

  const handleItemClick = useCallback(
    (index, isParent) => {
      if (isMobile) {
        if (isParent) {
          setActiveItem(index === activeItem ? null : index);
        } else {
          toggleDrawer();
          setActiveItem(null);
        }
      } else {
        setActiveItem(index === activeItem ? null : index);
      }
    },
    [setActiveItem, activeItem, isMobile, toggleDrawer]
  );

  if (loading) {
    return null;
  }

  return (
    <Styled.Nav
      component="nav"
      onFocus={openMenu}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      <Styled.Branding>
        <Logo />
      </Styled.Branding>

      <Styled.Menu>
        {menu.map((menuItem, index) => {
          if (!releaseFinanceHub && (menuItem.itemId === FINANCE_HUB)) {
            return null;
          }
          if (releaseFinanceHub
            && (menuItem.itemId === MONEY_PRO
              || menuItem.itemId === INVOICES
              || menuItem.itemId === BANKING
              || menuItem.itemId === TAX_SERVICES)) {
            return null;
          }

          const maybeExpand = activeItem === index;
          const isExpanded = expanded && maybeExpand;
          const { eventProps, ...menuItemProps } = menuItem;

          return (
            <MenuItem
              eventProps={{
                ...eventProps,
                elementAsset: menuItem.icon,
                elementName: menuItem.itemId ? `entity_nav_item_${menuItem.itemId}` : undefined,
                elementText: menuItem.title
              }}
              expanded={isExpanded}
              key={menuItem.title}
              onClick={(isParent = false) => handleItemClick(index, isParent)}
              {...menuItemProps}
            />
          );
        })}
      </Styled.Menu>
    </Styled.Nav>
  );
}

export default BusinessEntityNav;
